import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useState } from "react";
import { ftThemePreference } from "../constants/Constants";

// Theme preference is per device (localStorage), so it applies before login
// and on public pages too. "system" follows the OS light/dark setting.
export const THEME_PREFERENCES = ["system", "light", "dark"];

const THEME_COLOR = { light: "#f2f0ec", dark: "#121413" };

const darkQuery = window.matchMedia("(prefers-color-scheme: dark)");

const ThemeContext = createContext({ preference: "system", theme: "light", setPreference: () => {} });

function readPreference() {
    try {
        const p = localStorage.getItem(ftThemePreference);
        return p === "light" || p === "dark" ? p : "system";
    } catch {
        return "system";
    }
}

const resolveTheme = (preference, systemDark) =>
    preference === "system" ? (systemDark ? "dark" : "light") : preference;

// Keep this in step with the inline script in public/index.html, which does the
// same before first paint so a dark-mode user never sees a light flash.
function applyTheme(theme) {
    document.documentElement.dataset.theme = theme;
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", THEME_COLOR[theme]);
}

// Resolved value of a theme token (e.g. "--border") for places that can't take
// a CSS var — canvas drawing, color math. Call during render with `theme` in
// scope/deps so it re-reads after a switch; data-theme is always updated first.
export function readCssVar(name) {
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

export function ThemeProvider({ children }) {
    const [preference, setPreferenceState] = useState(readPreference);
    const [systemDark, setSystemDark] = useState(darkQuery.matches);
    const theme = resolveTheme(preference, systemDark);

    // Apply before children paint. The handlers below also apply synchronously,
    // ahead of their state update, so readCssVar() is never a render behind.
    useLayoutEffect(() => {
        applyTheme(theme);
    }, [theme]);

    useEffect(() => {
        const onSystemChange = (e) => {
            if (readPreference() === "system") applyTheme(e.matches ? "dark" : "light");
            setSystemDark(e.matches);
        };
        // Another tab changed the preference.
        const onStorage = (e) => {
            if (e.key !== ftThemePreference && e.key !== null) return;
            const next = readPreference();
            applyTheme(resolveTheme(next, darkQuery.matches));
            setPreferenceState(next);
        };
        darkQuery.addEventListener("change", onSystemChange);
        window.addEventListener("storage", onStorage);
        return () => {
            darkQuery.removeEventListener("change", onSystemChange);
            window.removeEventListener("storage", onStorage);
        };
    }, []);

    const setPreference = useCallback((next) => {
        try {
            if (next === "system") localStorage.removeItem(ftThemePreference);
            else localStorage.setItem(ftThemePreference, next);
        } catch {}
        applyTheme(resolveTheme(next, darkQuery.matches));
        setPreferenceState(next);
    }, []);

    return (
        <ThemeContext.Provider value={{ preference, theme, setPreference }}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    return useContext(ThemeContext);
}
